import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield, Lock, Globe, Server, CheckCircle, ArrowRight,
  Database, MapPin, Building2, FileCheck, Eye, RefreshCw
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const TRUST_BADGES = [
  { label: 'ISO 27001 Certified', icon: FileCheck, color: '#0066CC' },
  { label: 'GDPR Compliant', icon: Shield, color: '#14b8a6' },
  { label: 'SAP BTP Infrastructure', icon: Server, color: '#8b5cf6' },
  { label: 'EU Data Residency', icon: MapPin, color: '#f59e0b' },
];

const SECURITY_PILLARS = [
  {
    icon: Lock,
    title: 'Confidentiality',
    desc: 'Rigorous protocols ensure all client data is stored and transmitted with the highest levels of encryption and access control.',
  },
  {
    icon: Eye,
    title: 'Availability',
    desc: 'Enterprise-grade SLAs backed by SAP BTP infrastructure. Your compliance platform is accessible whenever you need it.',
  },
  {
    icon: Database,
    title: 'Integrity',
    desc: 'Data validation at every layer — from ERP extraction through to tax authority submission. No silent corruption, no data drift.',
  },
  {
    icon: RefreshCw,
    title: 'Continuous Compliance',
    desc: 'We work closely with cybersecurity experts to ensure our platform stays ahead of evolving threats and regulatory requirements.',
  },
];

const SecurityPage = () => (
  <PageLayout>
    {/* Hero */}
    <section className="pb-16 pt-4" data-testid="security-hero">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-6">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            Security & Trust
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-6">
            Security & data privacy<br />
            <span className="taxera-gradient-text">by design.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            At Taxera Technologies, security is embedded into the architecture of our platform — not bolted on as an afterthought.
            We are a Europe-based company, meticulously designed to ensure full GDPR compliance and prioritize our clients' data privacy.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Trust Badges */}
    <section className="pb-20" data-testid="trust-badges">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST_BADGES.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={badge.label}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`badge-${i}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${badge.color}12` }}>
                  <Icon className="w-6 h-6" style={{ color: badge.color }} strokeWidth={1.5} />
                </div>
                <span className="text-sm font-bold text-taxera-charcoal leading-snug">{badge.label}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Data Sovereignty */}
    <section className="pb-24" data-testid="data-sovereignty">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
              <Globe className="w-3.5 h-3.5" /> Data Sovereignty
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-5 leading-tight">
              Your data, your rules.<br />Full control over where it lives.
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              We offer the flexibility to select the country, data center, and hyperscaler responsible for processing your data,
              giving you complete control and peace of mind. As a Europe-based company, GDPR compliance is not an add-on — it's foundational.
            </p>
            <div className="space-y-3">
              {[
                'Choose your data center location and hyperscaler',
                'Full GDPR compliance by design, not retrofit',
                'Data residency options across EU jurisdictions',
                'Complete transparency on data processing and storage',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="bg-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(20,163,184,0.15)_0%,transparent_60%)]" />
              <div className="relative z-10">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'EU', label: 'Data Residency', sub: 'European jurisdictions' },
                    { value: '100%', label: 'GDPR Compliance', sub: 'By design' },
                    { value: 'Your', label: 'Choice of Hyperscaler', sub: 'AWS, Azure, GCP' },
                    { value: 'Zero', label: 'Data Shared', sub: 'With third parties' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="font-heading text-2xl font-bold text-taxera-teal mb-1">{stat.value}</div>
                      <div className="text-xs font-semibold text-white">{stat.label}</div>
                      <div className="text-2xs text-slate-500 mt-0.5">{stat.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* SAP BTP Infrastructure */}
    <section className="pb-24 bg-slate-50/50" data-testid="sap-btp-section">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 text-violet-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
            <Server className="w-3.5 h-3.5" /> Infrastructure
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-4 leading-tight">
            Enterprise-grade infrastructure<br />powered by SAP BTP
          </h2>
          <p className="text-base text-slate-600 leading-relaxed">
            We partner with SAP Business Technology Platform to deliver a global, enterprise-grade foundation
            that scales with your compliance needs.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Globe,
              title: 'Global Scale',
              desc: 'SAP BTP enables us to scale our tax technology solutions to meet the needs of multinationals operating across every jurisdiction worldwide.',
              color: '#0066CC',
            },
            {
              icon: Database,
              title: 'Backup & Disaster Recovery',
              desc: 'Enterprise-grade backup and disaster recovery capabilities ensure your compliance data is always safe, available, and recoverable.',
              color: '#14b8a6',
            },
            {
              icon: RefreshCw,
              title: 'Continuous Innovation',
              desc: 'With SAP BTP as our foundation, we continuously improve our platform, delivering world-class solutions that keep you ahead of regulatory change.',
              color: '#8b5cf6',
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${item.color}12` }}>
                  <Icon className="w-5 h-5" style={{ color: item.color }} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Security Posture */}
    <section className="pb-24" data-testid="security-posture">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wide rounded-full mb-4">
            <Lock className="w-3.5 h-3.5" /> Security
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-4 leading-tight">
            Highest standards in security
          </h2>
          <p className="text-base text-slate-600 leading-relaxed">
            We work closely with cybersecurity experts and continuously update our platform
            to stay compliant with the latest security standards and protect against evolving threats.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6">
          {SECURITY_PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-taxera-blue" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-1">{pillar.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{pillar.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ISO 27001 Highlight */}
    <section className="pb-24" data-testid="iso-27001">
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-slate-900 rounded-3xl relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(0,102,204,0.12)_0%,transparent_60%)]" />
          <div className="relative z-10 p-10 md:p-16 lg:p-20">
            <div className="grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wide rounded-full mb-5">
                  <FileCheck className="w-3.5 h-3.5" /> Certification
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                  ISO 27001 Certified
                </h2>
                <p className="text-base text-slate-400 leading-relaxed mb-4">
                  Taxera Technologies is proud to have achieved ISO 27001 certification — the globally recognized
                  standard for information security management systems.
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  This certification is a testament to our unwavering commitment to security and our dedication
                  to ensuring the confidentiality, integrity, and availability of our clients' data. We see it
                  as a recognition of the hard work and dedication of our team in providing a secure and reliable
                  tax technology platform for businesses around the world.
                </p>
              </div>
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-full bg-white/5 border border-white/10 flex flex-col items-center justify-center">
                  <Shield className="w-16 h-16 text-taxera-teal mb-3" strokeWidth={1} />
                  <div className="font-heading text-lg font-bold text-white">ISO 27001</div>
                  <div className="text-2xs text-slate-500 uppercase tracking-widest mt-1">Certified</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="pb-24" data-testid="security-cta">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-4">
          Ready to see the platform in action?
        </h2>
        <p className="text-base text-slate-500 mb-8">
          Security is just the foundation. Discover how Taxera transforms your global tax compliance.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/evaluate"
            className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105"
            data-testid="security-cta-evaluate"
          >
            Take the Diagnostic
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/platform"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white border border-slate-200 text-taxera-charcoal rounded-full font-semibold hover:border-slate-300 transition-all"
            data-testid="security-cta-platform"
          >
            Explore the Platform
          </Link>
        </div>
      </div>
    </section>
  </PageLayout>
);

export default SecurityPage;
